// frontend/src/components/common/MaintenanceChecker.js
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';

/**
 * MaintenanceChecker component checks if maintenance mode is active
 * and redirects authenticated users to the maintenance page.
 */
const MaintenanceChecker = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);

  // Poll the maintenance flag on a stable interval. This effect intentionally
  // does NOT depend on the current route, so navigating between pages no longer
  // triggers a refetch or resets the polling timer.
  useEffect(() => {
    let cancelled = false;

    const checkMaintenanceMode = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings/public`);
        const data = await response.json();

        if (!cancelled && response.ok) {
          const isMaintenanceActive = data.data.maintenance_mode === true || data.data.maintenance_mode === 'true';
          setMaintenanceMode(isMaintenanceActive);
        }
      } catch (error) {
        console.error('Error checking maintenance mode:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    checkMaintenanceMode();

    // Check maintenance mode every 2 minutes
    const interval = setInterval(checkMaintenanceMode, 120000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Handle redirects based on the latest known maintenance state and the
  // current route. Runs without fetching, so it's cheap on every navigation.
  useEffect(() => {
    // If maintenance mode is active and user is logged in
    // Dev users are exempt from maintenance mode redirects
    if (maintenanceMode && currentUser && currentUser.role !== 'dev') {
      // Don't redirect if already on maintenance page or settings page
      if (location.pathname !== '/maintenance' && location.pathname !== '/settings') {
        navigate('/maintenance', { replace: true });
      }
    }

    // If maintenance mode is off and user is on maintenance page, redirect to dashboard
    if (!maintenanceMode && location.pathname === '/maintenance' && currentUser) {
      navigate('/dashboard', { replace: true });
    }
  }, [maintenanceMode, currentUser, location.pathname, navigate]);

  // Don't block rendering, just handle redirects in the background
  return children;
};

export default MaintenanceChecker;
